import { useEffect, useMemo, useState } from "react";
import { useMultiTableData } from "./hooks/useMultiTableData.jsx";
import { useSnackbar } from "notistack";
import {
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  FileDownloadOutlined,
  AddCircleOutlineOutlined,
  LockOutlined,
  EditOutlined as EditIcon,
} from "@mui/icons-material";
import DataTable from "../DataTable.jsx";
import { getDatumZeit } from "../../services/formatService.js";
import { useUser } from "../../hooks/user.js";
import { getUserRowsPerPage } from "../../utils/userSettings.js";
import { useClaimAttachmentActions } from "../../hooks/useClaimAttachmentActions.jsx";
import PropTypes from "prop-types";
import { colorsFoundation } from "../../theme/index.js";
import AttachmentTitleModal from "../modals/AttachmentTitleModal.jsx";

const initialPageSize = 5;

export default function AttachmentsTable({
  schaden_id,
  onRowClick = () => {},
  onClickNew = () => {},
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUser();
  const [isTitleModalOpen, setIsTitleModalOpen] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const { state: attachmentsState, setState: setAttachmentsState } =
    useClaimAttachmentActions();

  const tableKey = "anlagen";
  const storageKey = "v2_table_anlagen";
  const defaultRowsPerPage = getUserRowsPerPage(user, 5);

  const rows = attachmentsState?.data || [];

  const tablesConfig = useMemo(
    () => [
      {
        key: tableKey,
        label: "Anlagen",
        route: `/data/anlages`,
        exportRoute: `/data/anlages/export`,
        exportFilename: `anlagen_schaden_${schaden_id}`,
        responseKey: tableKey,
        pageSize: defaultRowsPerPage,
        storageKey: storageKey,
        extraParams: {
          filters: {
            schaden_id: schaden_id,
          },
        },
        onStateChange: setAttachmentsState,
      },
    ],
    [schaden_id, defaultRowsPerPage, storageKey]
  );

  const {
    tableStates,
    updateTableState,
    fetchData,
    exportData,
    tableExportStates,
  } = useMultiTableData(tablesConfig, enqueueSnackbar);

  const tableState = tableStates[tableKey] || {};

  useEffect(() => {
    fetchData(tableKey);
  }, [schaden_id]);

  const formatFileSize = (bytes) => {
    if (!bytes) return "-";
    const kb = bytes / 1024;
    if (kb < 1024) {
      return `${kb.toFixed(1)} KB`;
    }
    const mb = kb / 1024;
    return `${mb.toFixed(1)} MB`;
  };

  const handleEdit = (attachment, event) => {
    if (event?.stopPropagation) {
      event.stopPropagation();
    }
    setSelectedAttachment(attachment);
    setIsTitleModalOpen(true);
  };

  const anlagenHeaderDefinition = [
    {
      accessorKey: "icons",
      header: "",
      size: 10,
      enableSorting: false,
      enableColumnFilter: false,
      muiTableBodyCellProps: () => {
        return { align: "center" };
      },
      Cell: (tableCellObject, passedItem) => {
        const objectToRender = tableCellObject
          ? tableCellObject.row.original
          : passedItem;
        return (
          <span>
            {objectToRender.intern ? (
              <Tooltip title="intern (für VN nicht sichtbar" key="new">
                <LockOutlined sx={{ fontSize: "1.5rem" }} />
              </Tooltip>
            ) : null}
          </span>
        );
      },
    },
    {
      accessorKey: "filename",
      header: "Dateiname",
      size: 250,
      Cell: ({ cell }) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {cell.getValue()}
        </Typography>
      ),
    },
    {
      accessorKey: "bezeichnung",
      header: "Bezeichnung",
      size: 200,
    },
    {
      accessorKey: "filetype",
      header: "Typ",
      size: 80,
    },
    {
      accessorFn: (row) => row.filesize,
      header: "Größe",
      size: 100,
      Cell: ({ row }) => formatFileSize(row.original.filesize),
    },
    {
      accessorKey: "created_at",
      header: "Hochgeladen am",
      size: 150,
      Cell: ({ cell }) => getDatumZeit(cell.getValue()),
    },
    // column for edit action button
    {
      header: "Aktionen",
      size: 50,
      enableSorting: false,
      enableColumnFilter: false,
      Cell: ({ row }) => (
        <Tooltip title="Bezeichnung bearbeiten">
          <IconButton
            size="small"
            onClick={(event) => handleEdit(row.original, event)}
            sx={{ color: colorsFoundation.neutral[600] }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const handleExportExcel = () => {
    exportData(tableKey);
  };

  const toolbarActions = [
    {
      type: "button",
      label: "Neue Anlage",
      icon: <AddCircleOutlineOutlined />,
      onClick: onClickNew,
      variant: "primary",
    },
    {
      label: "Excel Export",
      type: "icon",
      variant: "tertiary",
      icon: tableExportStates[tableKey].isExporting ? (
        <CircularProgress color="primary" size={32} thickness={5} />
      ) : (
        <FileDownloadOutlined />
      ),
      onClick: handleExportExcel,
      disabled: tableExportStates[tableKey].isExporting,
    },
  ];

  return (
    <>
      <DataTable
        columns={anlagenHeaderDefinition}
        data={rows}
        isLoading={tableState.isLoading}
        hasLoaded={tableState.hasLoaded}
        pageIndex={tableState.pageIndex}
        pageSize={tableState.pageSize}
        totalRows={tableState.totalRows}
        onChangeSorting={(sorting) => updateTableState(tableKey, { sorting })}
        onChangeFilters={(filters) => updateTableState(tableKey, { filters })}
        onChangePage={(nextOrUpdater) => {
          const base = {
            pageIndex: tableState.pageIndex ?? 0,
            pageSize: tableState.pageSize ?? initialPageSize,
          };
          const next =
            typeof nextOrUpdater === "function"
              ? nextOrUpdater(base)
              : nextOrUpdater;
          if (!next) {
            return;
          }
          updateTableState(tableKey, {
            pageIndex: next.pageIndex,
            pageSize: next.pageSize,
          });
        }}
        storageKey={storageKey}
        enableHeader
        enableSorting
        enableGlobalFilter={true}
        enableColumnFilters={true}
        enableColumnHiding={true}
        topToolbarIcons={true}
        enableTopToolbar={true}
        toolbarActions={toolbarActions}
        onRowClick={onRowClick}
      />

      <AttachmentTitleModal
        open={isTitleModalOpen}
        onClose={() => {
          setIsTitleModalOpen(false);
          setSelectedAttachment(null);
        }}
        attachment={selectedAttachment}
      />
    </>
  );
}

AttachmentsTable.propTypes = {
  schaden_id: PropTypes.oneOfType([
    PropTypes.number.isRequired,
    PropTypes.string.isRequired,
  ]),
  onRowClick: PropTypes.func,
  onClickNewCommunication: PropTypes.func,
};
